import { Text, Box, Flex, Link, Image, Button } from "@chakra-ui/react";
import { SsoSection } from "./SsoSection";
import { PaymentValueProps } from "../../payments/components/PaymentValueProps";
import { ImmutableArray } from "@hookstate/core";
import { t } from "i18next";
import { FUNNEL_TYPES } from "../../funnel/utils/contants";
import { FunnelComponentProps } from "../../funnel/types";
import { useIsSmallBreakpoint } from "../../../utils/useBreakpoints";
import { SignUpWithEmailPasswordForm } from "./SignUpWithEmailPasswordForm";
import { DividerWithText } from "../../payments/components/DividerWithText";
import { TermsSection } from "./TermsSection";
import { LoginOptionsDivider } from "./LoginOptionsDivider";
import { AuthType } from "../../../hooks/useAuthMutations";
import { FunnelSteps } from "../../funnel/FunnelSteps";

export function SignUpModalFooter({
  setFunnelType,
  largeLoginCTA = false,
}: FunnelComponentProps & { largeLoginCTA?: boolean }) {
  if (largeLoginCTA) {
    return null;
  }
  return (
    <Flex padding="24px" justifyContent="center">
      <Text fontSize="sm" color="neutral.300" align="center">
        {t("components.auth.alreadyHaveAnAccount")}{" "}
        <Link
          color="neutral.200"
          textDecoration="underline"
          onClick={() => {
            setFunnelType(FUNNEL_TYPES.login);
          }}
        >
          {t("components.auth.login")}
        </Link>
      </Text>
    </Flex>
  );
}

export function SignUpModalBody({
  onSuccess,
  setFunnelType,
  valueProps,
  welcomeText = t("components.auth.welcomeText.welcomeToGlobalComix"),
  invalidate = true,
  triggeredByStartNextRelease = false,
  showGraphic = false,
  onClose,
  showEmailPasswordForm = false,
  centerWelcomeText = false,
  autoFocus = true,
  largeLoginCTA = false,
  spinning = false,
  loadingText,
  shouldShowSteps,
  stepCount,
  currentStep,
  stepText,
  stepDisplayText,
  showStepIndicators,
}: FunnelComponentProps & {
  valueProps: ImmutableArray<string>;
  welcomeText?: string;
  invalidate?: boolean;
  spinning?: boolean;
  triggeredByStartNextRelease?: boolean;
  showCreateFreeAccountButton?: boolean;
  showGraphic?: boolean;
  showEmailPasswordForm?: boolean;
  centerWelcomeText?: boolean;
  autoFocus?: boolean;
  largeLoginCTA?: boolean;
  loadingText?: string;
}) {
  const isSmall = useIsSmallBreakpoint();

  return (
    <Flex direction="column" margin="auto" width="100%" padding="24px">
      {shouldShowSteps ? (
        <FunnelSteps
          stepCount={stepCount}
          currentStep={currentStep}
          stepText={stepText}
          text={stepDisplayText}
          hideProgressIndicators={!showStepIndicators}
        />
      ) : (
        <>
          {showGraphic && (
            <Image
              width={{ base: "100%", md: "80%", lg: "465px" }}
              src={`${window.gc.global.assets_url}/static/images/web-ui/signup_modal_image.png`}
              alignSelf="center"
              mb="24px"
            />
          )}
          {welcomeText ? (
            <Text
              fontFamily="Bricolage Grotesque"
              fontWeight={showGraphic ? "600" : "500"}
              lineHeight="31.2px"
              fontSize="24px"
              textAlign={
                isSmall || showGraphic || centerWelcomeText
                  ? "center"
                  : undefined
              }
              color="dune.dune"
              maxWidth={showGraphic ? "365px" : undefined}
              alignSelf={showGraphic ? "center" : undefined}
              pb="24px"
            >
              {welcomeText}
            </Text>
          ) : null}

          {triggeredByStartNextRelease && (
            <Box
              width="100%"
              height="1px"
              background="transparent.white.10"
              mb="24px"
            />
          )}

          {valueProps && valueProps.length > 0 && (
            <>
              <Box alignSelf={isSmall ? "center" : undefined}>
                <PaymentValueProps
                  valueProps={valueProps}
                  showSeparators={false}
                />
              </Box>
              <Box height="24px" />
              <Box
                width="100%"
                height="1px"
                background="transparent.white.10"
              />
              <Box height="24px" />
            </>
          )}
        </>
      )}
      {showEmailPasswordForm && (
        <>
          <SignUpWithEmailPasswordForm
            autoFocus={autoFocus}
            onSuccess={onSuccess}
            invalidate={invalidate}
            loading={spinning}
            loadingText={loadingText}
          />
          <DividerWithText text="or" padding="16px 0px" />
        </>
      )}
      <SsoSection
        setFunnelType={setFunnelType}
        onClose={onClose}
        triggeredByStartNextRelease={triggeredByStartNextRelease}
        isAccountCreationCTATestEnabled={triggeredByStartNextRelease}
        hideEmail={showEmailPasswordForm}
        authType={AuthType.signup}
        onAuthenticationSuccess={onSuccess}
      />
      {largeLoginCTA && (
        <Flex direction="column" paddingTop="16px">
          <LoginOptionsDivider />
          <Button
            variant="tertiary"
            width="full"
            size="lg"
            onClick={() => setFunnelType(FUNNEL_TYPES.login)}
          >
            {t("components.auth.login")}
          </Button>
        </Flex>
      )}
      {showEmailPasswordForm && <TermsSection />}
    </Flex>
  );
}
